import { OperationListService } from './../shared/operation-list.service';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { B3SelectItem } from 'src/app/shared/models/b3-select-item.model';
import { OperationTransferenceService } from 'src/app/services/operation/operation-transference.service';
import { OperationFilter } from '../../shared/operation-filter';
import {
  formatFilterDate,
  isEmptyFilterValue,
  toDatepickerValue,
} from '../../shared/operation-filter.utils';
import { normalizeOperationTypeForApi, normalizeOperationTypeForForm } from '../../shared/operation-type-code.utils';

@Component({
  standalone: false,
  selector: 'app-operation-list-filter',
  templateUrl: './operation-list-filter.component.html',
  styleUrls: ['./operation-list-filter.component.scss']
})
export class OperationListFilterComponent implements OnInit, OnChanges {
  @Output() isOpenChange = new EventEmitter<boolean>();
  @Output() closed: EventEmitter<any> = new EventEmitter();

  @Input() get isOpen(): boolean {
    return this._isOpen;
  }

  set isOpen(value: boolean) {
    this._isOpen = value;
    this.isOpenChange.emit(this._isOpen);
  }

  private _isOpen: boolean = false;

  formFilter: FormGroup = new FormGroup({});
  operationType: B3SelectItem[] = [];
  datepickerResetKey = 0;
  periodStartDate: Date | null = null;
  periodEndDate: Date | null = null;
  invalidPeriodDate = false;

  constructor(
    private formBuilder: FormBuilder,
    private operationTransferenceService: OperationTransferenceService,
    private operationListService: OperationListService,
  ) { }

  ngOnInit(): void {
    this.operationType = this.operationTransferenceService.getTypeOptions();
    this.setForm();
    this.loadFormFromFilters();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen']?.currentValue === true) {
      this.loadFormFromFilters();
    }
  }

  get minFinalDateIso(): string {
    return this.periodStartDate ? this.toIsoDate(this.periodStartDate) : '';
  }

  get maxInitialDateIso(): string {
    return this.periodEndDate ? this.toIsoDate(this.periodEndDate) : '';
  }

  setForm(): void {
    this.formFilter = this.formBuilder.group({
      code: [''],
      holderAccount: [''],
      purchaseAccount: [''],
      carbonCreditCode: [''],
      type: [null],
      initialDate: [null],
      finalDate: [null],
    });

    this.formFilter.get('initialDate')?.valueChanges.subscribe(value => {
      this.periodStartDate = isEmptyFilterValue(value) ? null : toDatepickerValue(value);
      this.revalidatePeriodDates();
    });

    this.formFilter.get('finalDate')?.valueChanges.subscribe(value => {
      this.periodEndDate = isEmptyFilterValue(value) ? null : toDatepickerValue(value);
      this.revalidatePeriodDates();
    });
  }

  loadFormFromFilters(): void {
    const filters = this.operationListService.filters.getValue();
    const showStoredValues = this.operationListService.personalizedFilterActive;
    const hasInitialDate = showStoredValues && !isEmptyFilterValue(filters.initialDate);
    const hasFinalDate = showStoredValues && !isEmptyFilterValue(filters.finalDate);

    this.formFilter.setValue({
      holderAccount: showStoredValues ? (filters.holderCode ?? '') : '',
      purchaseAccount: showStoredValues ? (filters.purchaserCode ?? '') : '',
      carbonCreditCode: showStoredValues ? (filters.carbonCreditCode ?? '') : '',
      code: showStoredValues ? (filters.operationCode ?? '') : '',
      type: showStoredValues ? this.resolveOperationTypeOption(filters.typeCode) : null,
      initialDate: hasInitialDate ? toDatepickerValue(filters.initialDate) : null,
      finalDate: hasFinalDate ? toDatepickerValue(filters.finalDate) : null,
    });

    this.syncPeriodDatesFromForm();
  }

  cleanForm(): void {
    this.formFilter.reset({
      code: '',
      holderAccount: '',
      purchaseAccount: '',
      carbonCreditCode: '',
      type: null,
      initialDate: null,
      finalDate: null,
    });
    this.formFilter.get('initialDate')?.setValue(null);
    this.formFilter.get('finalDate')?.setValue(null);
    this.resetPeriodDateValidation();
    this.refreshDatepickerViews();
  }

  trackDatepickerResetKey(_index: number, key: number): number {
    return key;
  }

  filter(): void {
    this.syncPeriodDatesFromForm();

    if (this.invalidPeriodDate) {
      return;
    }

    this.operationListService.setFilters(this.buildFiltersFromForm());
    this.operationListService.isSetDate.next(true);
    this.close();
  }

  close(): void {
    this.closed.emit();
  }

  onInitialDateChange(event: Event): void {
    this.onDateChange(event, 'initialDate');
  }

  onFinalDateChange(event: Event): void {
    this.onDateChange(event, 'finalDate');
  }

  private onDateChange(event: Event, controlName: 'initialDate' | 'finalDate'): void {
    const iso = (event as CustomEvent<{ value?: string }>).detail?.value?.trim() ?? '';

    if (!iso) {
      this.formFilter.get(controlName)?.setValue(null);
      return;
    }

    const date = toDatepickerValue(iso);

    if (!date) {
      return;
    }

    this.formFilter.get(controlName)?.setValue(date);
  }

  private syncPeriodDatesFromForm(): void {
    const initialDate = this.formFilter.get('initialDate')?.value;
    const finalDate = this.formFilter.get('finalDate')?.value;

    this.periodStartDate = isEmptyFilterValue(initialDate) ? null : toDatepickerValue(initialDate);
    this.periodEndDate = isEmptyFilterValue(finalDate) ? null : toDatepickerValue(finalDate);
    this.revalidatePeriodDates();
  }

  private revalidatePeriodDates(): void {
    this.invalidPeriodDate = !!this.periodStartDate
      && !!this.periodEndDate
      && this.periodStartDate > this.periodEndDate;
  }

  private resetPeriodDateValidation(): void {
    this.periodStartDate = null;
    this.periodEndDate = null;
    this.invalidPeriodDate = false;
  }

  private toIsoDate(date: Date): string {
    return formatFilterDate(date) ?? '';
  }

  private refreshDatepickerViews(): void {
    this.datepickerResetKey++;
  }

  private buildFiltersFromForm(): OperationFilter {
    const formValues = this.formFilter.getRawValue();

    return {
      operationCode: formValues.code || undefined,
      carbonCreditCode: formValues.carbonCreditCode || undefined,
      holderCode: formValues.holderAccount || undefined,
      purchaserCode: formValues.purchaseAccount || undefined,
      typeCode: normalizeOperationTypeForApi(formValues.type),
      initialDate: isEmptyFilterValue(formValues.initialDate)
        ? undefined
        : formatFilterDate(formValues.initialDate),
      finalDate: isEmptyFilterValue(formValues.finalDate)
        ? undefined
        : formatFilterDate(formValues.finalDate),
    };
  }

  private resolveOperationTypeOption(typeCode?: string | null): B3SelectItem | null {
    const normalizedType = normalizeOperationTypeForForm(typeCode);

    if (!normalizedType) {
      return null;
    }

    return this.operationType.find(option => option.inputId === normalizedType) ?? null;
  }
}
